import { countries } from '../constants/countries';
import adminDivisions from '../constants/country-admin-divisions';
import { getFullCountryName, getFullStateName } from './countries';

// Map of all available sources (functions and constants)
const SOURCES = {
  getFullCountryName,
  getFullStateName,
  adminDivisions,
  countries,
  env: {
    supportEmail: process.env.REACT_APP_SUPPORT_EMAIL,
    complianceEmail: process.env.REACT_APP_COMPLIANCE_EMAIL || 'compliance@checkmate.tech',
    consentStatement: process.env.REACT_APP_CANDIDATE_CONSENT_STATEMENT,
    policeVettingConsentForm: process.env.REACT_APP_POLICE_VETTING_CONSENT_FORM,
    afpConsentForm: process.env.REACT_APP_AFP_CONSENT_FORM,
    usFmcsaPspConsentForm:
      process.env.REACT_APP_US_FMCSA_PSP_CONSENT_FORM ||
      'https://cdn.checkmate.tech/checkmate-files/assets/us_fmcsa_psp_consent_form.pdf',
    dotEmploymentVerificationForm:
      process.env.REACT_APP_DOT_EMPLOYMENT_VERIFICATION_FORM ||
      'https://cdn.checkmate.tech/checkmate-files/assets/dot_employment_verification_form.pdf',
    checkmateNameINT: process.env.REACT_APP_CHECKMATE_NAME,
    checkmateNameAU: process.env.REACT_APP_CHECKMATE_NAME_AU,
    checkmateNameUS: process.env.REACT_APP_CHECKMATE_NAME_US,
  },
  // Add other sources here as needed
};

/**
 * Get a nested value from constants by a dotted path that may include bracket lookups.
 * Example: adminDivisions.AU_STATES[VIC].name -> "Victoria"
 *
 * When constants use flat dotted keys (e.g. hiddenValues["role_location.role_location_name"]),
 * a single-segment path like "role_location_name" is resolved by finding a key that equals
 * the path or ends with "." + path, so {{role_location_name}} works.
 */
const resolveTemplatePath = (path, constants) => {
  if (!path || typeof path !== 'string') return null;

  const trimmed = path.trim();
  const parts = trimmed.split('.');

  // Nested lookup: constants.key.subkey
  // When resolving into objects that may be camelCased (e.g. selected option from API), try camelCase key if snake_case key is missing
  let value = constants?.[parts[0]];
  if (value != null) {
    for (let i = 1; i < parts.length; i++) {
      if (value == null) return null;
      if (parts[i].includes('[')) {
        const [arrayName, lookup] = parts[i].split('[');
        const lookupValue = lookup.replace(']', '');
        value = value[arrayName]?.find(item => item.value === lookupValue);
        continue;
      }
      const key = parts[i];
      const next = value[key];
      if (next !== undefined && next !== null) {
        value = next;
      } else {
        const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        value = value[camelKey];
      }
    }
    if (value != null) return value;
  }

  // Flat dotted key: constants["role_location.role_location_name"] when path is "role_location_name"
  if (constants != null && parts.length === 1) {
    if (trimmed in constants) return constants[trimmed];
    for (const k of Object.keys(constants)) {
      if (k.endsWith('.' + trimmed)) {
        const v = constants[k];
        if (v !== undefined && v !== null) return v;
      }
    }
  }

  return null;
};

/**
 * Resolve a single branch (path or quoted string) for expression evaluation.
 */
const resolveBranch = (branch, sources) => {
  const s = (branch && typeof branch === 'string' ? branch : '').trim();
  if (
    s.length >= 2 &&
    ((s.startsWith("'") && s.endsWith("'")) || (s.startsWith('"') && s.endsWith('"')))
  ) {
    return s.slice(1, -1);
  }
  const value = resolveTemplatePath(s, sources);
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/**
 * Evaluate a simple expression inside {{ }}: ternary (a ? b : c) or fallback (a || b).
 * Otherwise resolves as a path.
 */
const evaluateTemplateExpression = (content, sources) => {
  const expr = (content && typeof content === 'string' ? content : '').trim();
  if (!expr) return '';

  // Ternary: condition ? trueBranch : falseBranch
  const ternaryIdx = expr.indexOf(' ? ');
  const colonIdx = expr.indexOf(' : ');
  if (ternaryIdx !== -1 && colonIdx > ternaryIdx) {
    const condition = expr.slice(0, ternaryIdx).trim();
    const rest = expr.slice(ternaryIdx + 3).trim();
    const colonInRest = rest.indexOf(' : ');
    const trueBranch = colonInRest !== -1 ? rest.slice(0, colonInRest).trim() : rest;
    const falseBranch = colonInRest !== -1 ? rest.slice(colonInRest + 3).trim() : '';
    const condValue = resolveTemplatePath(condition, sources);
    const truthy = condValue != null && condValue !== '' && condValue !== false;
    return resolveBranch(truthy ? trueBranch : falseBranch, sources);
  }

  // Fallback: a || b || c (first truthy). If no path/literal exists in sources yet,
  // return null so callers can preserve {{ }} for a later round (e.g. form values).
  if (expr.includes(' || ')) {
    const parts = expr.split(' || ').map(p => p.trim());
    let sawDefined = false;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (
        part.length >= 2 &&
        ((part.startsWith("'") && part.endsWith("'")) ||
          (part.startsWith('"') && part.endsWith('"')))
      ) {
        const literal = part.slice(1, -1);
        if (literal !== '') return literal;
        sawDefined = true;
        continue;
      }
      const raw = resolveTemplatePath(part, sources);
      if (raw == null) continue;
      sawDefined = true;
      if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
      const asString = String(raw);
      if (asString !== '') return asString;
    }
    return sawDefined ? '' : null;
  }

  // Plain path – return null when path not found so callers can leave {{ }} in place (2nd round) or replace with ''
  const value = resolveTemplatePath(expr, sources);
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value;
  return String(value);
};

/**
 * Replaces template variables in strings.
 * 1. Expression-style: {{ a ? b : c }} and {{ a || b || c }} (evaluated first).
 * 2. Path lookups: {{company.name}}, {{role_location_name}} (including flat dotted keys).
 * When replaceUnresolvedWithEmpty is false: leave {{ }} in place when path not found (candidate fields 2nd round).
 * When replaceUnresolvedWithEmpty is true: replace unresolved placeholders with '' (config fields single round).
 */
const replaceConstantTemplates = (template, sources = {}, replaceUnresolvedWithEmpty = false) => {
  if (typeof template !== 'string' || !template.includes('{{')) return template;

  const pattern = /\{\{((?:(?!\{\{).)*?)\}\}/g;
  let result = template;
  let prev;
  let iteration = 0;

  do {
    prev = result;
    iteration++;
    result = result.replace(pattern, (match, content) => {
      try {
        if (!content) return match;
        const resolvedContent = replaceConstantTemplates(
          content.trim(),
          sources,
          replaceUnresolvedWithEmpty,
        );
        const value = evaluateTemplateExpression(resolvedContent, sources);
        if (value == null) {
          return replaceUnresolvedWithEmpty ? '' : match;
        }
        return String(value);
      } catch (error) {
        return match;
      }
    });

    if (iteration > 10) break;
  } while (result !== prev && result.includes('{{'));

  return result;
};

/**
 * Process template placeholders in input (string, array, or object).
 * - preserveOnNull: when true, leave unresolved {{ }} in place (candidate fields: can be resolved in a later round).
 *   Return the partially resolved string so successful replacements are not discarded when some placeholders remain.
 * - replaceUnresolvedWithEmpty: when true, replace unresolved {{ path }} with '' (config fields: single round).
 */
export const processTemplates = (
  input,
  sources = {},
  preserveOnNull = false,
  replaceUnresolvedWithEmpty = false,
) => {
  if (input == null) return input;

  const sourcesMap = { ...SOURCES, ...sources };

  if (typeof input === 'string') {
    const result = replaceConstantTemplates(input, sourcesMap, replaceUnresolvedWithEmpty);
    return result;
  }

  if (Array.isArray(input)) {
    return input.map(item =>
      processTemplates(item, sourcesMap, preserveOnNull, replaceUnresolvedWithEmpty),
    );
  }

  if (typeof input === 'object') {
    return Object.entries(input).reduce((acc, [k, v]) => {
      if (typeof v === 'string') {
        if (v.startsWith('{{') && v.endsWith('}}')) {
          try {
            const content = v.slice(2, -2).trim();
            const resolvedContent = replaceConstantTemplates(
              content,
              sourcesMap,
              replaceUnresolvedWithEmpty,
            );
            const value = evaluateTemplateExpression(resolvedContent, sourcesMap);
            if (Array.isArray(value)) {
              acc[k] = value;
            } else {
              acc[k] = value == null ? (replaceUnresolvedWithEmpty ? '' : v) : String(value);
            }
          } catch (error) {
            acc[k] = v;
          }
        } else if (v.includes('{{')) {
          acc[k] = processTemplates(v, sourcesMap, preserveOnNull, replaceUnresolvedWithEmpty);
        } else {
          acc[k] = v;
        }
      } else if (Array.isArray(v) || (v && typeof v === 'object')) {
        acc[k] = processTemplates(v, sourcesMap, preserveOnNull, replaceUnresolvedWithEmpty);
      } else {
        acc[k] = v;
      }
      return acc;
    }, {});
  }

  return input;
};
